interface OrganizationClientStart {
    id: string;
    name: string;
    /** The workspace list types this as a Date but sends an ISO string. */
    createdAt: Date | string;
}

interface ToOrganizationHistoryStartParams {
    organizationCreatedAt: Date | undefined;
    /** The firm's client workspaces, undefined while they load. */
    clients: readonly OrganizationClientStart[] | undefined;
    selectedClientIds: readonly string[] | undefined;
}

/** When the firm's Activity history starts, and the client whose joining starts it, if not the firm's. */
export interface OrganizationHistoryStart {
    at: Date;
    clientName: string | undefined;
}

/**
 * When the firm Activity chart's history starts: the earliest of the firm's
 * creation and its clients' creation, since a client can join Kick before the
 * firm and its activity must stay visible. With a client filter only those
 * clients' creation counts, because the chart then only holds their activity
 * and firm changes that add or remove them. The firm wins a tie. Undefined
 * until the firm and its clients load.
 */
export function toOrganizationHistoryStart({
    organizationCreatedAt,
    clients,
    selectedClientIds,
}: ToOrganizationHistoryStartParams): OrganizationHistoryStart | undefined {
    if (!organizationCreatedAt || !clients) {
        return undefined;
    }
    const selectedIds = new Set(selectedClientIds);
    const selectedClients = clients.filter(({ id }) => selectedIds.has(id));
    const candidates: OrganizationHistoryStart[] =
        selectedClients.length > 0
            ? selectedClients.map(toClientStart)
            : [
                  {
                      at: new Date(organizationCreatedAt),
                      clientName: undefined,
                  },
                  ...clients.map(toClientStart),
              ];
    return candidates.reduce((earliest, candidate) =>
        candidate.at < earliest.at ? candidate : earliest,
    );
}

const toClientStart = ({
    name,
    createdAt,
}: OrganizationClientStart): OrganizationHistoryStart => ({
    at: new Date(createdAt),
    clientName: name,
});

/** The Before Kick zone's sentence: when the firm started on Kick, or when the client that starts its history joined. */
export const describeOrganizationHistoryStart =
    (clientName: string | undefined) => (start: string) =>
        clientName === undefined
            ? `Your activity starts on ${start}, when your firm started on Kick.`
            : `Your activity starts on ${start}, when ${clientName} joined Kick.`;
